import { useEffect, useState } from "react";
import { Badge } from "@mantine/core";
import type { DataTableColumn } from "mantine-datatable";
import { Link } from "react-router";

import BasePage from "../../BasePage";
import ResponsiveTable from "../../../components/ResponsiveTable";
import { TableOverlay } from "../../../components/LoadingOverlay";
import { coreApi } from "../../../utils/CoreAPI";
import { ChangeLevel, ChangeStatus } from "../../../interfaces/Change";

function ChangeManagementPage() {
    const [loading, setLoading] = useState(true);
    const [changes, setChanges] = useState<any[]>([]);

    const columns: DataTableColumn<any>[] = [
        {
            accessor: "created_at",
            title: "Submitted On",
            sortable: true,
            render: (record) => {
                return new Date(record.created_at).toLocaleDateString();
            },
        },
        {
            accessor: "title",
            title: "Title",
            sortable: true,
            render: (record) => {
                const url = `changes/${record.change_id}`;

                return (
                    <Link to={url} state={{ currentChange: record }}>
                        {record.title}
                    </Link>
                );
            },
        },
        {
            accessor: "requester",
            title: "Requester",
            sortable: true,
        },
        {
            accessor: "status",
            title: "Status",
            sortable: true,
            render: (record) => {
                switch (record.status) {
                    case ChangeStatus.Open:
                        return <Badge color="green">Open</Badge>;
                    case ChangeStatus.OnHold:
                        return <Badge color="orange">On Hold</Badge>;
                    case ChangeStatus.Closed:
                        return <Badge color="red">Closed</Badge>;
                    default:
                        return <Badge>NULL</Badge>;
                }
            },
        },
        {
            accessor: "priority",
            title: "Priority",
            sortable: true,
            render: (record) => {
                switch (record.priority) {
                    case ChangeLevel.Low:
                        return <Badge color="blue">Low</Badge>;
                    case ChangeLevel.Medium:
                        return <Badge color="orange">Medium</Badge>;
                    case ChangeLevel.High:
                        return <Badge color="red">High</Badge>;
                    case ChangeLevel.Urgent:
                        return <Badge color="red">!Urgent!</Badge>;
                    default:
                        return <Badge>NULL</Badge>;
                }
            },
        },
        {
            accessor: "approver",
            title: "Assigned To",
            sortable: true,
        },
    ];

    useEffect(() => {
        const getChanges = async () => {
            const response = await coreApi.get("/changes");

            setChanges(response.data);
            setLoading(false);
        };

        getChanges();
    }, []);

    return (
        <BasePage
            title="Change Management"
            desc="Submit, view and approve change requests."
            breadcrumbs={[{ label: "Change Management" }]}
            fullWidth
        >
            <TableOverlay visible={loading}>
                <ResponsiveTable
                    data={changes}
                    columns={columns}
                    gridRowId={(row) => row.change_id}
                />
            </TableOverlay>
        </BasePage>
    );
}

export default ChangeManagementPage;